import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CHART_FRAMEWORK_HINTS, CHART_FRAMEWORK_LABELS, CHART_TEMPLATES, LEDGER_FRAMEWORKS, type ChartSwitchPreview, type ChartTree, type LedgerFramework } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { InfoHint } from '../../components/InfoHint';
import { useSession } from '../../session';
import './chart.css';

/**
 * Einrichtung → Kontenplan (KON-162): den Kontenrahmen wechseln. Wirkia Standard ist ein kleiner Plan für Einzelunternehmen, SKR03 und SKR04 sind Auszüge der üblichen
 * Rahmen. Vor dem Wechsel zeigt die Seite, was passiert: Verwendete Konten bleiben (als Altkonten oder mit den Angaben des neuen Rahmens), eigene Konten bleiben auf
 * Wunsch, alles andere wird durch den neuen Vorschlag ersetzt.
 */
export function ChartSetupPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('setup.write') && can('chart.write');
  const [chosen, setChosen] = useState<LedgerFramework | null>(null);
  const [keepCustom, setKeepCustom] = useState(true);
  const [resetTaxAccounts, setResetTaxAccounts] = useState(true);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const chart = useQuery({ queryKey: ['chart'], queryFn: async () => (await api<{ data: ChartTree }>('GET', '/chart')).data });
  const current = chart.data?.framework ?? null;
  const target = chosen ?? current;
  const preview = useQuery({
    queryKey: ['chart-switch-preview', target, keepCustom, resetTaxAccounts],
    queryFn: async () => (await api<{ data: ChartSwitchPreview }>('POST', '/chart/switch/preview', { framework: target, keepCustom, resetTaxAccounts })).data,
    enabled: !!target && canWrite,
  });

  const apply = useMutation({
    mutationFn: async () => (await api<{ data: ChartTree }>('PUT', '/chart/switch', { framework: target, keepCustom, resetTaxAccounts })).data,
    onSuccess: (tree) => {
      client.setQueryData(['chart'], tree);
      client.invalidateQueries({ queryKey: ['chart-options'] });
      client.invalidateQueries({ queryKey: ['finance-settings'] });
      setDone(`Der Kontenplan beruht jetzt auf ${CHART_FRAMEWORK_LABELS[tree.framework]}.`);
      setChosen(null);
      setError(null);
    },
    onError: (e) => {
      setDone(null);
      setError(e instanceof ApiError ? e.message : 'Der Wechsel hat nicht geklappt.');
    },
  });

  if (chart.isLoading) return <p className="muted">Wird geladen …</p>;
  const p = preview.data;
  const same = target === current;

  return (
    <div className="chart-setup">
      <section className="card">
        <h2>Kontenplan</h2>
        <p>
          Der Kontenplan ordnet Ihre Konten in Kategorien. Er beginnt mit einem Vorschlag und gehört dann Ihnen: Sie ändern, ergänzen und löschen Konten im{' '}
          <Link to="/kontenplan">Kontenplan</Link>. Aktuell: <strong>{current ? CHART_FRAMEWORK_LABELS[current] : '–'}</strong>.
        </p>
        {done && <div className="alert alert--ok" role="status">{done}</div>}
        {error && <div className="alert alert--error" role="alert">{error}</div>}

        <fieldset className="chart-frameworks" disabled={!canWrite}>
          <legend>Kontenrahmen<InfoHint label="Kontenrahmen">Der Kontenrahmen gibt vor, welche Konten mit welchen Nummern es gibt. Ihr Steuerberater weiß, welchen er nutzt. Wenn er keinen vorgibt, nehmen Sie Wirkia Standard.</InfoHint></legend>
          {LEDGER_FRAMEWORKS.map((f) => (
            <label key={f} className={`chart-framework${target === f ? ' is-chosen' : ''}`}>
              <input type="radio" name="framework" checked={target === f} onChange={() => setChosen(f)} />
              <span className="chart-framework__body">
                <strong>{CHART_FRAMEWORK_LABELS[f]}{f === current ? ' (aktuell)' : ''}</strong>
                <span className="muted small">{CHART_FRAMEWORK_HINTS[f]}</span>
                <span className="muted small">{CHART_TEMPLATES[f].accounts.length} Konten im Vorschlag</span>
              </span>
            </label>
          ))}
        </fieldset>
        {!canWrite && <p className="muted small">Den Kontenrahmen wechselt, wer die Einrichtung ändern und den Kontenplan pflegen darf.</p>}

        {canWrite && (
          <>
            <div className="chart-options">
              <label className="check">
                <input type="checkbox" checked={keepCustom} onChange={(e) => setKeepCustom(e.target.checked)} />
                <span>Eigene Konten behalten<InfoHint label="Eigene Konten">Konten, die Sie angelegt oder bearbeitet haben, bleiben beim Wechsel stehen. Ohne Häkchen entfallen sie, wenn sie nicht verwendet werden.</InfoHint></span>
              </label>
              <label className="check">
                <input type="checkbox" checked={resetTaxAccounts} onChange={(e) => setResetTaxAccounts(e.target.checked)} />
                <span>Konten je Steuerschlüssel auf den neuen Rahmen setzen<InfoHint label="Konten je Steuerschlüssel">Erlös- und Steuerkonto je Steuerschlüssel (Einrichtung → Finanzen) bekommen die Vorgaben des neuen Rahmens, zum Beispiel 8400 und 1776 bei SKR03. Ohne Häkchen bleiben Ihre Einstellungen.</InfoHint></span>
              </label>
            </div>

            {p && !same && (
              <div className="chart-preview" role="status" aria-label="Was beim Wechsel passiert">
                <h3>Was passiert</h3>
                <ul>
                  <li><strong>{p.create}</strong> Konten aus {CHART_FRAMEWORK_LABELS[p.framework]} entstehen neu.</li>
                  <li><strong>{p.remove}</strong> unverwendete Konten des bisherigen Vorschlags entfallen.</li>
                  {p.adopt > 0 && <li><strong>{p.adopt}</strong> verwendete Konten bleiben und übernehmen Bezeichnung und Kategorie des neuen Rahmens.</li>}
                  {p.keepUsed > 0 && <li><strong>{p.keepUsed}</strong> verwendete Konten, die der neue Rahmen nicht kennt, bleiben als inaktive Altkonten, damit alte Belege eindeutig bleiben.</li>}
                  {keepCustom && p.keepCustom > 0 && <li><strong>{p.keepCustom}</strong> eigene Konten bleiben.</li>}
                  {p.conflicts.length > 0 && <li>Die Nummern {p.conflicts.slice(0, 8).join(', ')}{p.conflicts.length > 8 ? ' …' : ''} trägt schon ein bleibendes Konto. Das bleibende gilt.</li>}
                  {p.taxAccounts > 0 && <li>Die Konten für {p.taxAccounts} Steuerschlüssel werden neu gesetzt.</li>}
                </ul>
                <p className="muted small">Belege und Rechnungen ändern sich nicht. Der Wechsel lässt sich später wieder umkehren, bearbeitete Konten bleiben dann aber bestehen.</p>
              </div>
            )}
            {same && <p className="muted small">Wählen Sie einen anderen Kontenrahmen, um den Wechsel zu planen.</p>}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={same || apply.isPending || !p}
                onClick={() => window.confirm(`Kontenrahmen auf ${CHART_FRAMEWORK_LABELS[target!]} wechseln? Verwendete Konten und Belege bleiben erhalten.`) && apply.mutate()}
              >
                {apply.isPending ? 'Wird gewechselt …' : 'Kontenrahmen wechseln'}
              </button>
            </div>
          </>
        )}
      </section>
      <section className="card">
        <h2>Hinweise</h2>
        <ul className="chart-hints">
          <li>SKR03 und SKR04 sind <strong>Auszüge</strong> mit den gängigen Konten, nicht der vollständige Rahmen. Den vollständigen Rahmen können Sie im Kontenplan per CSV importieren.</li>
          <li>Prüfen Sie die Konten je Steuerschlüssel mit Ihrem Steuerberater, bevor Sie Daten an ihn übergeben.</li>
          <li>Verwendete Konten lassen sich nicht löschen und nicht umnummerieren. Sie lassen sich deaktivieren.</li>
        </ul>
      </section>
    </div>
  );
}
